import React, { useState, useEffect, useRef } from "react";
import { useSudowriteStore } from "../../stores/sudowriteStore";
import { MUSE2_ALPHA_STYLE_FIELD, resolveDraftStyleSample } from "../WriteSettings/muse2Alpha";
import Textarea from "../StoryBible/Textarea";
import { UserStyleExamplesContainer } from "./styles";

const MAX_WORDS = 1000;
// The Muse 2 alpha judges style adherence, so its candidates take a longer
// sample, siloed in its own field: shared across the four codenames but never
// mixed into the sample the regular prose modes read. The server applies the
// matching 3,000-word allowance to alpha Draft requests in ChapterGenerator.
const MUSE2_ALPHA_MAX_WORDS = 3000;

const UserStyleExamples = ({ muse2Alpha = false }) => {
  // Retrieve userData and its setter from the Sudowrite store.
  const userData = useSudowriteStore((state) => state.userData);
  const fieldName = muse2Alpha ? MUSE2_ALPHA_STYLE_FIELD : "userStyleExamples";
  const maxWords = muse2Alpha ? MUSE2_ALPHA_MAX_WORDS : MAX_WORDS;

  // What Draft would send for this silo right now — for the alpha this falls
  // back to the legacy sample until an alpha-specific one is saved.
  const userStyleExamples = resolveDraftStyleSample(userData?.data, muse2Alpha);

  // Local state seeds from the stored value verbatim — deliberately without
  // truncating to this silo's cap. localValue can then differ from the store
  // only through a user edit (Textarea's setValue fires only on input), which
  // makes the !== checks below an edit gate: an untouched box never writes, so
  // a stored sample longer than this silo's cap is displayed rather than being
  // truncated and silently written back. The cap applies to edits, in the
  // Textarea and again on the server.
  const [localValue, setLocalValue] = useState(userStyleExamples);

  // Update function that persists changes to the store.
  const updateUserStyleExamples = (newValue) => {
    userData.update({ [fieldName]: newValue });
  };

  // Debounce effect: saves localValue to store 2 seconds after the last update.
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only update the store if the local value differs.
      if (localValue !== userStyleExamples) {
        updateUserStyleExamples(localValue);
      }
    }, 2000);

    // Cleanup the timer if localValue changes before 2 seconds elapse.
    return () => clearTimeout(timer);
  }, [localValue, userStyleExamples]);

  // The component remounts when the silo changes (keyed by the parent), which
  // drops any debounce still pending. Flush the edit to the field it was typed
  // in on unmount, so switching models within the debounce window neither loses
  // the edit nor writes it into the other silo's field. Safe for an untouched
  // box: localValue only differs from the store after a user edit.
  const flushRef = useRef();
  flushRef.current = () => {
    if (localValue !== userStyleExamples) {
      updateUserStyleExamples(localValue);
    }
  };
  useEffect(() => {
    return () => flushRef.current?.();
  }, []);

  return (
    <UserStyleExamplesContainer className="user-style-examples">
      <div className="title">
        Your Style Examples <span className="optional">(optional)</span>
      </div>
      <div className="description">Improve this prose mode's style by showing it examples of what you want.</div>
      <Textarea
        maxWords={maxWords}
        placeholder={`Show the AI up to ${maxWords} words representative of your writing style. Unrelated samples should go on their own lines. For best results, use the same tense and POV as your story.`}
        value={localValue}
        setValue={setLocalValue}
        // Deliberately not writing state here. Textarea calls onChange with the
        // raw text BEFORE setFormattedContent truncates, and when the truncated
        // result equals the previous value setFormattedContent returns early
        // without calling setValue — so a raw write from onChange would survive
        // (type word 3,001 at the cap, or bulk-paste past it, and the excess is
        // retained). setValue from setFormattedContent is the single writer.
        onChange={() => {}}
        height="short"
        hideWordCount={false}
      />
    </UserStyleExamplesContainer>
  );
};

export default UserStyleExamples;
